import { useState } from 'react';
import {
  Card,
  CurrencyFormat,
  Icon,
  NoFound,
  Spinner,
} from '@/components/generic';
import { copyToClipboard } from '@/utils/string';
import { useGetOnchainUserTxs } from '@package/smart-wallet/hooks';
import { ethers } from 'ethers';

// Simple date formatter
const formatDate = (timestamp: number | string | Date | undefined) => {
  if (!timestamp) return 'N/A';
  const date = new Date(timestamp);
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const WithdrawalsTable = () => {
  const [pageNum, setPageNum] = useState(1);
  const pageSize = 10;

  // Get onchain transactions
  const { isLoading, records, total } = useGetOnchainUserTxs({
    pageNum,
    pageSize: 10, // Get more records to filter
    type: 'REFERRAL_WITHDRAWAL',
  });

  const totalPages = Math.ceil((total || 0) / pageSize);

  const handlePreviousPage = () => {
    if (pageNum > 1) {
      setPageNum(pageNum - 1);
    }
  };

  const handleNextPage = () => {
    if (pageNum < totalPages) {
      setPageNum(pageNum + 1);
    }
  };

  if (isLoading) {
    return (
      <Card title='Withdrawal History' className='mb-3'>
        <div className='flex items-center justify-center py-12'>
          <Spinner />
        </div>
      </Card>
    );
  }

  if (!records || records.length === 0) {
    return (
      <Card title='Withdrawal History' className='mb-3'>
        <div className='p-5'>
          <NoFound
            title='No withdrawals yet'
            sub='Your referral reward withdrawal history will appear here once you claim your first rewards.'
          />
        </div>
      </Card>
    );
  }

  return (
    <Card title='Withdrawal History' className='mb-3'>
      <div className='overflow-x-auto'>
        <table className='w-full text-body-2 [&_th]:h-17 [&_th,&_td]:pl-5 [&_th,&_td]:py-4 [&_th,&_td]:first:pl-4 [&_th,&_td]:last:pr-4 [&_th]:align-middle [&_th]:text-left [&_th]:text-caption [&_th]:text-t-tertiary/80 [&_th]:font-normal'>
          <thead>
            <tr>
              <th>Transaction ID</th>
              <th>Date</th>
              <th>Amount</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {records.map((tx: any) => (
              <tr key={tx.id}>
                {/* Transaction ID */}
                <td>
                  <div className='flex items-center gap-2'>
                    <div className='text-body-2 text-t-primary font-mono text-xs'>
                      {tx.orderNo
                        ? `${tx.orderNo.slice(0, 6)}...${tx.orderNo.slice(-4)}`
                        : 'N/A'}
                    </div>
                    {tx.orderNo && (
                      <button
                        onClick={() => copyToClipboard(tx.orderNo)}
                        className='hover:scale-110 transition-transform'
                      >
                        <Icon
                          className='w-3.5 h-3.5 fill-t-tertiary hover:fill-t-primary'
                          name='copy'
                        />
                      </button>
                    )}
                  </div>
                </td>

                {/* Date */}
                <td>
                  <div className='text-body-2 text-t-secondary whitespace-nowrap'>
                    {formatDate(tx.createTime || tx.createdAt)}
                  </div>
                </td>

                {/* Amount */}
                <td>
                  <div className='font-semibold text-t-primary'>
                    <CurrencyFormat
                      value={Number(ethers.formatUnits(tx.amount))}
                    />
                  </div>
                </td>

                {/* Status */}
                <td>
                  <div
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-caption font-medium ${
                      tx.status === 'COMPLETED' || tx.status === 'SUCCESS'
                        ? 'bg-green-500/10 text-green-600'
                        : tx.status === 'PROCESSING' || tx.status === 'PENDING'
                          ? 'bg-yellow-500/10 text-yellow-600'
                          : tx.status === 'FAILED'
                            ? 'bg-red-500/10 text-red-600'
                            : 'bg-gray-500/10 text-gray-600'
                    }`}
                  >
                    <div
                      className={`w-1.5 h-1.5 rounded-full ${
                        tx.status === 'COMPLETED' || tx.status === 'SUCCESS'
                          ? 'bg-green-600'
                          : tx.status === 'PROCESSING' ||
                              tx.status === 'PENDING'
                            ? 'bg-yellow-600'
                            : tx.status === 'FAILED'
                              ? 'bg-red-600'
                              : 'bg-gray-600'
                      }`}
                    />
                    {tx.status}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className='flex items-center justify-between px-5 py-4 border-t border-s-stroke2'>
          <div className='text-caption text-t-tertiary'>
            Page {pageNum} of {totalPages}
          </div>
          <div className='flex items-center gap-2'>
            <button
              onClick={handlePreviousPage}
              disabled={pageNum === 1}
              className='flex items-center justify-center w-8 h-8 rounded-lg border border-s-stroke2 hover:bg-b-surface2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
            >
              <Icon
                className='w-4 h-4 fill-t-secondary rotate-90'
                name='arrow'
              />
            </button>
            <button
              onClick={handleNextPage}
              disabled={pageNum === totalPages}
              className='flex items-center justify-center w-8 h-8 rounded-lg border border-s-stroke2 hover:bg-b-surface2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
            >
              <Icon
                className='w-4 h-4 fill-t-secondary -rotate-90'
                name='arrow'
              />
            </button>
          </div>
        </div>
      )}
    </Card>
  );
};

export default WithdrawalsTable;
